import { useEffect, useRef, useState } from 'react';
import type { Editor } from '../editor';
import { completeCommand } from '../commands/registry';

/**
 * 명령행 (작업지시서 P2-05 / P2-14)
 *
 * CAD 사용자는 여기에 손이 산다. 아이콘을 누르는 게 아니라 `L↵` 을 친다.
 *
 * 동작 규약 (AutoCAD 와 동일해야 한다):
 *   · Enter / Space  — 입력 확정. 비어 있으면 **직전 명령 반복**
 *   · Esc            — 명령 취소
 *   · ↑ / ↓          — 입력 이력 탐색
 *   · Tab            — 자동완성 채택
 */
export function CommandLine({ editor }: { editor: Editor }) {
  const [text, setText] = useState('');
  const [tick, setTick] = useState(0);
  const [histIndex, setHistIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const submitted = useRef<string[]>([]);

  useEffect(() => editor.subscribe(() => setTick((t) => t + 1)), [editor]);

  // 명령이 시작/종료될 때 입력창에 포커스를 되돌린다
  useEffect(() => {
    inputRef.current?.focus();
  }, [tick === 0]);

  const snap = editor.snapshot();
  const suggestions = editor.isRunning ? [] : completeCommand(text);

  function submit(value: string) {
    if (value.trim() !== '') {
      submitted.current.push(value.trim());
      if (submitted.current.length > 100) submitted.current.shift();
    }
    setHistIndex(-1);
    setText('');
    editor.submitText(value);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // 명령행 안에서는 전역 키 가드가 아니라 여기서 처리한다
    if (e.key === 'Enter') {
      e.preventDefault();
      // selection 프롬프트에서 Enter = 선택 확정
      if (editor.activePrompt?.type === 'selection' && text.trim() === '') {
        editor.confirmSelection();
        return;
      }
      submit(text);
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setText('');
      editor.feed({ t: 'esc' });
      return;
    }

    if (e.key === ' ' && text.trim() === '') {
      // 빈 상태의 Space = Enter (직전 명령 반복)
      e.preventDefault();
      if (editor.activePrompt?.type === 'selection') editor.confirmSelection();
      else submit('');
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      const first = suggestions[0];
      if (first) setText(first.name);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const h = submitted.current;
      if (h.length === 0) return;
      const next = histIndex < 0 ? h.length - 1 : Math.max(0, histIndex - 1);
      setHistIndex(next);
      setText(h[next] ?? '');
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const h = submitted.current;
      if (histIndex < 0) return;
      const next = histIndex + 1;
      if (next >= h.length) {
        setHistIndex(-1);
        setText('');
      } else {
        setHistIndex(next);
        setText(h[next] ?? '');
      }
    }
  }

  const recent = editor.log.slice(-4);

  return (
    <div className="cmdline">
      <div className="cmdline__log" aria-live="polite">
        {recent.map((line, i) => (
          <div key={`${i}-${line}`} className="cmdline__log-line">
            {line}
          </div>
        ))}
      </div>

      <div className="cmdline__row">
        <span className="cmdline__prompt" title="현재 요구 사항">
          {snap.promptMessage}
          {snap.keywords.length > 0 && (
            <span className="cmdline__keywords">
              {' '}
              [{snap.keywords.map((k) => `${k[0]}=${k}`).join(' / ')}]
            </span>
          )}
          :
        </span>
        <input
          ref={inputRef}
          data-cmdline="true"
          className="cmdline__input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={editor.isRunning ? '' : '명령 또는 좌표 입력 (예: L, @1200,0, 1500<45)'}
          aria-label="명령 입력"
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {suggestions.length > 0 && text.trim() !== '' && (
        <div className="cmdline__suggest">
          {suggestions.map((c) => (
            <button
              key={c.name}
              type="button"
              className="cmdline__suggest-item"
              title={c.hint ? `${c.label} — ${c.hint}` : c.label}
              onMouseDown={(e) => {
                e.preventDefault();
                submit(c.name);
              }}
            >
              <b>{c.name}</b>
              <span className="cmdline__suggest-alias">{c.aliases.join(', ')}</span>
              <span className="cmdline__suggest-label">{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
